import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { Box, type Theme, Typography, useMediaQuery } from "@mui/material";
import type { GridColumnVisibilityModel } from "@mui/x-data-grid";
import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { addNurseTableSortingKey } from "../../services/LocalStorageService";
import { CONTROL_HEIGHT, FONT_SIZE, INK, MOTION, NEUTRAL, STAFF_PICKER } from "../../theme/tokens";
import { EmploymentType, type NurseData } from "../../utils/models";
import type { ShiftAvailabilityWindow } from "../../utils/shiftAvailability";
import { getEmploymentTypeDisplayName } from "../../utils/utilFunctions";
import { NURSE_TYPE_FIELD } from "../NursesMatrixGrid/columnVisibility";
import type { NursesMatrixRowId, NursesMatrixSelectionMode } from "../NursesMatrixGrid/consts";
import { NursesMatrixGrid } from "../NursesMatrixGrid/NursesMatrixGrid";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";
import { EmptyState } from "../UIToolkit/molecules/EmptyState";
import { StepHeader } from "../UIToolkit/molecules/StepHeader";
import type { RowGroupingOptions } from "../UIToolkit/Tables/SystemDataGrid/types";
import type { AddStaffListScope } from "./useAddStaffList";

const AVAILABLE_GROUP = "available";
const BLOCKED_GROUP = "blocked";

/** The design lists each nurse's contract beside the name, so this grid starts with the Nurse Type column on. */
const ADD_STAFF_COLUMN_VISIBILITY: GridColumnVisibilityModel = { [NURSE_TYPE_FIELD]: true };

/**
 * Step 2 unlocking is the panel's one orchestrated moment: the list rises into place as the first shift is
 * picked (it mounts only then). Opacity + a short lift only; reduced motion keeps it static.
 */
const UNLOCK_ENTRANCE_SX = {
	"@keyframes addStaffListEnter": {
		from: { opacity: 0, transform: `translateY(${MOTION.VIEW_SLIDE_PX}px)` },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `addStaffListEnter ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
} as const;

const CONTRACT_OPTIONS = Object.values(EmploymentType).map((type) => ({ value: type, label: getEmploymentTypeDisplayName(type) }));
const ALL_CONTRACT_VALUES = CONTRACT_OPTIONS.map((option) => option.value);

/** The filter button matches the 40px search box and Sort/Columns buttons beside it. */
const CONTRACT_TRIGGER_SX = { minHeight: CONTROL_HEIGHT.STANDARD, boxSizing: "border-box" } as const;

type Props = {
	window: ShiftAvailabilityWindow | null;
	staffingDate: DateTime;
	rows: NurseData[];
	availableCount: number;
	dateRange: DateTime[];
	dayNo: number;
	selectionMode: NursesMatrixSelectionMode;
	selectedRowIds: NursesMatrixRowId[];
	onSelectionChange: (rows: NurseData[]) => void;
	loading: boolean;
	contractTypes: EmploymentType[];
	onContractTypesChange: (value: EmploymentType[]) => void;
	/** The job / role the list is scoped to — names the empty state when nobody here holds it. */
	scope: AddStaffListScope | null;
	/** False when nobody in the department holds `scope` at all (not just nobody matching the filters). */
	hasAnyHolder: boolean;
};

/**
 * "· 24 available" after the step title. The hours and the date already sit right above it (the picked
 * card, the header's date) and in the footer's read-back, so the heading carries only the count — which
 * keeps it short enough for the search box beside it to be wide.
 */
const StepMeta: FC<{ availableCount: number }> = ({ availableCount }) => {
	const { t } = useTranslation();
	return (
		<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, whiteSpace: "nowrap" }} data-testid="add-nurse-step-staff-meta">
			<Box component="span" aria-hidden sx={{ color: NEUTRAL[450] }}>
				·
			</Box>
			<Box component="span" sx={{ fontWeight: 600, color: INK.PRIMARY, fontVariantNumeric: "tabular-nums" }}>
				{t("add.staff.available", { count: availableCount })}
			</Box>
		</Box>
	);
};

/** The search takes the toolbar's spare room between these widths; the heading keeps its own width. */
const SEARCH_WIDTH_RANGE = { min: STAFF_PICKER.SEARCH_MIN_WIDTH, max: STAFF_PICKER.SEARCH_MAX_WIDTH } as const;

/**
 * The heading never grows: the spare room goes to the search first (up to its max), and only what is left
 * becomes the auto margin that pushes the filters to the row's end. On a narrow panel the controls wrap
 * below the heading as a group.
 */
const STEP_HEADING_SX = { flex: "0 1 auto", marginInlineEnd: "auto" } as const;

/** "No one here has the RN job yet" when nobody holds the scope; otherwise it's the search / filters. */
const describeEmptyList = (scope: AddStaffListScope | null, hasAnyHolder: boolean, t: TFunction): string => {
	if (hasAnyHolder || !scope) return t("add.staff.empty");
	return scope.kind === "job" ? t("add.staff.empty.no.job", { job: scope.name }) : t("add.staff.empty.no.role", { role: scope.name });
};

/** Step 2 before a shift is picked: muted heading + an empty state saying what unlocks the list. */
const StaffStepLocked: FC = () => {
	const { t } = useTranslation();
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.75, flex: 1, minHeight: STAFF_PICKER.LIST_MIN_HEIGHT }} data-testid="add-nurse-staff-step">
			<StepHeader step={2} title={t("add.staff.step.staff")} disabled dataTestId="add-nurse-step-staff" />
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					border: `1px dashed ${NEUTRAL[400]}`,
					borderRadius: STAFF_PICKER.LOCKED_WELL_RADIUS,
					backgroundColor: NEUTRAL[50],
				}}
			>
				<EmptyState
					icon={<ScheduleOutlinedIcon />}
					title={t("add.staff.pick.shift.title")}
					hint={t("add.staff.pick.shift.hint")}
					dataTestId="add-nurse-pick-shift-empty"
				/>
			</Box>
		</Box>
	);
};

/** Available staff bandless on top; the struck "N can't work this shift" band folded below them. */
const useCantWorkGrouping = (): RowGroupingOptions<NurseData> => {
	const { t } = useTranslation();
	return useMemo(
		() => ({
			getGroupKey: (row) => (row.unavailableReason != null ? BLOCKED_GROUP : AVAILABLE_GROUP),
			bandlessKeys: [AVAILABLE_GROUP],
			groupOrder: [AVAILABLE_GROUP, BLOCKED_GROUP],
			defaultCollapsedKeys: [BLOCKED_GROUP],
			renderGroupLabel: (group) => (
				<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 500, color: INK.MUTED }}>
					{t("add.staff.cant.work", { count: group.rows.length })}
				</Typography>
			),
			getGroupAriaLabel: (group) => t("add.staff.cant.work", { count: group.rows.length }),
		}),
		[t],
	);
};

/**
 * The contract filter, labelled like the grid's own "Nurse Type" column. Nothing ticked = no contract
 * filter: the trigger then reads "Nurse Type All" instead of a count badge that would look like seven
 * active filters.
 */
const ContractFilter: FC<{ value: EmploymentType[]; onChange: (value: EmploymentType[]) => void }> = ({ value, onChange }) => {
	const { t } = useTranslation();
	return (
		<SystemDropdown
			data-testid="add-nurse-contract-type-dropdown"
			trigger="button"
			label={t("nurses.matrix.nurse.type")}
			placeholder={t("system.dropdown.all")}
			items={CONTRACT_OPTIONS}
			selectedItems={value.length === ALL_CONTRACT_VALUES.length ? [] : value}
			onChangeMultiple={(next) => onChange(next.length ? next : ALL_CONTRACT_VALUES)}
			selectSx={CONTRACT_TRIGGER_SX}
			multiple
		/>
	);
};

/**
 * Step 2 · "Who can work it?" — the staff picker for the picked window: available staff first, then a
 * folded "N can't work this shift" band with each reason. Locked behind step 1 until a shift is picked.
 */
export const StaffStep: FC<Props> = (props) => {
	const { window, staffingDate, rows, availableCount, dateRange, dayNo, selectionMode, selectedRowIds, onSelectionChange, loading } = props;
	const { t } = useTranslation();

	const rowGrouping = useCantWorkGrouping();
	// Below a desktop width the step heading, search, Sort, Columns and Nurse Type only share one row when Sort
	// and Columns drop their labels; a second toolbar row would cost the list a staff row.
	const iconOnlyToolbarControls = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

	if (!window) return <StaffStepLocked />;

	return (
		<Box
			sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: STAFF_PICKER.LIST_MIN_HEIGHT, ...UNLOCK_ENTRANCE_SX }}
			data-testid="add-nurse-staff-step"
		>
			<NursesMatrixGrid
				nursesData={rows}
				dateRange={dateRange}
				highlightDateIndexes={[dayNo]}
				isAdd={true}
				filterDate={staffingDate}
				localStorageTableKey={addNurseTableSortingKey}
				enableColumnPicker
				defaultColumnVisibility={ADD_STAFF_COLUMN_VISIBILITY}
				loading={loading}
				emptyState={<EmptyState title={describeEmptyList(props.scope, props.hasAnyHolder, t)} dataTestId="add-nurse-staff-empty" />}
				searchableColumns={["name"]}
				searchPlaceholder={t("add.staff.search")}
				searchWidth={SEARCH_WIDTH_RANGE}
				leadingToolbarNodes={
					<StepHeader
						step={2}
						title={t("add.staff.step.staff")}
						meta={<StepMeta availableCount={availableCount} />}
						dataTestId="add-nurse-step-staff"
						sx={STEP_HEADING_SX}
					/>
				}
				toolbarNodes={<ContractFilter value={props.contractTypes} onChange={props.onContractTypesChange} />}
				selectionMode={selectionMode}
				selectedRowIds={selectedRowIds}
				onSelectionChange={onSelectionChange}
				pendingDayIndex={dayNo}
				rowGrouping={rowGrouping}
				iconOnlyToolbarControls={iconOnlyToolbarControls}
				dataTestId="add-nurse-matrix-grid"
			/>
		</Box>
	);
};
